import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CloseIcon } from '../common/CloseIcon';
import { Trans, useTranslation } from 'react-i18next';
import ExpanderArrow from './ExpanderArrow';
import { CSSTransition } from 'react-transition-group';
import OffMarketSellIcon from '../FeedEvents/OffMarketSellIcon';
import OffMarketBuyIcon from '../FeedEvents/OffMarketBuyIcon';
import PropertyImageUpload, { ImageData } from '../common/PropertyImageUpload';
import { ImageLightbox } from '../ImageLightbox';
import { useOffMarketForm } from '../../hooks/useOffMarketForm';
import { usePriceEstimation } from '../../hooks/usePriceEstimation';
import { PriceEstimationReport } from '../PriceEstimation/PriceEstimationReport';
import BuyingTemplateIndicator from './BuyingTemplateIndicator';
import PreEstimationModal from './PreEstimationModal';
import PriceEstimationBanner from './PriceEstimationBanner';

import '../MessagesModal/MessagesModal.scss';
import './BuyingTemplateIndicator.scss';
import './OffMarketBuyAndSellDetailsModal.scss';

type OffMarketAction = 'buy' | 'sell';

export interface PropertyDetails {
  description?: string;
  size?: number;
  rooms?: string;
  floor?: number;
  hasElevator?: boolean;
  hasBalcony?: boolean;
  renovationStatus?: string;
  buildingType?: string;
  images?: ImageData[];
  askingPrice?: number;
  priceEstimationId?: string;
  priceEstimationStatus?: 'idle' | 'queued' | 'in_progress' | 'completed' | 'failed' | 'error';
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (propertyDetails?: PropertyDetails) => void;
  actionType: OffMarketAction;
  isLoading: boolean;
  isLoggedIn?: boolean;
  isLandingPage?: boolean;
  isEditMode?: boolean;
  initialData?: PropertyDetails;
  willingnessId?: string;
  setLoginModalVisible?: (isVisible: boolean) => void;
  // Building data for price estimation.
  building?: any;
  /** Lift the overlay above the ModalController top band (profile/landingMenu)
   *  when opened from inside a high-z modal such as the profile "My listings"
   *  view. Omitted by the create flow, which keeps the default z 2202. */
  elevated?: boolean;
};

const MAX_IMAGES = 30;

/**
 * Property-details dialog shown before a could-sell or could-buy intent is
 * submitted, and reused to edit an existing listing (`isEditMode`). Collects
 * the property details and hands them to `onConfirm`.
 */
const OffMarketBuyAndSellDetailsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  actionType,
  isLoading,
  isLoggedIn,
  isLandingPage,
  isEditMode,
  initialData,
  willingnessId,
  setLoginModalVisible,
  building,
  elevated
}) => {
  const { t } = useTranslation();
  const [sizeError, setSizeError] = useState<string>('');
  const [isPriceReportOpen, setIsPriceReportOpen] = useState(false);

  const [isDetailsExpanded, setIsDetailsExpanded] = useState<boolean>(isEditMode ? true : false);
  const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Gate restoration to run only once per modal open
  const uiRestoreAttemptedRef = useRef<boolean>(false);

  // Use the custom hook for form data and enhancement management
  const {
    formData,
    setFormData,
    enhancementStatus,
    setEnhancementStatus,
    isPurchasingCredits,
    handleBuyCredits,
    saveFormDataToLocalStorage,
    clearFormData,
    fetchEnhancementStatus,
    // Template-related
    hasTemplate,
    isLoadingTemplate,
    isUsingTemplate,
    shouldSaveTemplate,
    setShouldSaveTemplate,
    isTemplateAnimating,
    saveBuyingTemplate,
    deleteBuyingTemplate,
    // Image helpers
    toggleImageVersion,
    regenerateImage,
    isRegenerating
  } = useOffMarketForm({
    actionType,
    buildingId: willingnessId, // Use willingnessId as a unique identifier
    isLoggedIn: isLoggedIn || false,
    isOpenModal: isOpen,
    isLandingPage: isLandingPage || false // Pass isLandingPage to determine correct return URL
  });

  // Lightbox state
  const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
  const [lightboxInitialIndex, setLightboxInitialIndex] = useState<number>(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isPreEstimationModalOpen, setIsPreEstimationModalOpen] = useState<boolean>(false);
  const [forceShowPreFields, setForceShowPreFields] = useState<boolean>(false);

  const requestDelete = () => {
    setShowDeleteConfirm(true);
  };

  // Price estimation integration
  // Extract address from building prop (if available)
  const buildingAddress = React.useMemo(() => {
    if (building?.addresses && building.addresses.length > 0) {
      const addr = building.addresses[0];
      return {
        street: addr.street,
        houseNumber: addr.houseNumber,
        city: addr.city,
        postcode: addr.postcode,
        country: addr.country || 'FI',
        coordinates: building.geometry?.coordinates?.[0]?.[0] ? {
          lat: building.geometry.coordinates[0][0][1],
          lng: building.geometry.coordinates[0][0][0]
        } : undefined
      };
    }
    return {};
  }, [building]);

  const {
    estimation,
    status: estimationStatus,
    error: estimationError,
    estimationId,
    isPolling,
    startEstimation // Function to manually start estimation
  } = usePriceEstimation(
    {
      address: buildingAddress,
      description: formData.description,
      size: formData.size,
      rooms: formData.rooms,
      floor: formData.floor,
      hasElevator: formData.hasElevator,
      hasBalcony: formData.hasBalcony,
      renovationStatus: formData.renovationStatus,
      building_id: building?.id || willingnessId,
      willingness_id: willingnessId
    },
    isDetailsExpanded,
    isLoggedIn || false,
    buildingAddress
  );

  // Add askingPrice to formData
  const [askingPrice, setAskingPrice] = useState<string>('');

  // Initialize state with initial data when in edit mode
  useEffect(() => {
    if (isEditMode && initialData) {
      setFormData({
        description: initialData.description || '',
        size: initialData.size ? initialData.size.toString() : '',
        rooms: initialData.rooms || '',
        floor: typeof initialData.floor === 'number' ? initialData.floor.toString() : initialData.floor || '',
        hasElevator: initialData.hasElevator || false,
        hasBalcony: initialData.hasBalcony || false,
        renovationStatus: initialData.renovationStatus || '',
        buildingType: initialData.buildingType || '',
        images: Array.isArray(initialData.images)
          ? initialData.images.map((img: any) => ({ ...img }))
          : []
      });

      // Set asking price if provided
      if (initialData.askingPrice) {
        setAskingPrice(initialData.askingPrice.toString());
      }

      // Fetch enhancement status in edit mode for sell action
      if (actionType === 'sell' && isLoggedIn) {
        fetchEnhancementStatus();
      }
    }
  }, [isEditMode, initialData, setFormData, actionType, isLoggedIn, fetchEnhancementStatus]);

  // Check for payment return and restore details expanded state and scroll position
  useEffect(() => {
    if (isOpen && actionType === 'sell' && !uiRestoreAttemptedRef.current) {
      const detailsKey = `off_market_details_expanded_${actionType}_${willingnessId}`;
      const scrollKey = `off_market_scroll_position_${actionType}_${willingnessId}`;

      const savedDetailsState = localStorage.getItem(detailsKey);
      const savedScrollPosition = localStorage.getItem(scrollKey);

      // Only proceed if we actually have something to restore
      if (savedDetailsState === 'true' || savedScrollPosition) {
        // Mark as attempted to avoid duplicate work
        uiRestoreAttemptedRef.current = true;

        // Restore details expanded state
        if (savedDetailsState === 'true') {
          setIsDetailsExpanded(true);
          localStorage.removeItem(detailsKey);
        }

        // Restore scroll position
        if (savedScrollPosition && modalRef.current) {
          // If details are being expanded, wait for animation; otherwise restore quickly
          const waitTime = savedDetailsState === 'true' ? 400 : 120; // 300ms + buffer
          setTimeout(() => {
            if (!modalRef.current) return;
            const target = parseInt(savedScrollPosition, 10);
            let attempts = 0;
            const maxAttempts = 10; // ~1s retries
            const tryRestore = () => {
              if (!modalRef.current) return;
              modalRef.current.scrollTop = target;
              if (Math.abs(modalRef.current.scrollTop - target) > 2 && attempts < maxAttempts) {
                attempts += 1;
                setTimeout(tryRestore, 100);
              } else {
                localStorage.removeItem(scrollKey);
              }
            };
            tryRestore();
          }, waitTime);
        }
      }
    }

    // Reset the restoration gate when modal closes
    if (!isOpen) {
      uiRestoreAttemptedRef.current = false;
    }
  }, [isOpen, actionType, willingnessId]);

  // Handle buying credits with login check
  const handleBuyCreditsWithLoginCheck = () => {
    if (!isLoggedIn) {
      // Save form data and prompt login without closing this modal
      saveFormDataToLocalStorage();
      // Also save details expanded state
      if (isDetailsExpanded) {
        localStorage.setItem(`off_market_details_expanded_${actionType}_${willingnessId}`, 'true');
      }
      // Save scroll position
      if (modalRef.current) {
        const scrollPosition = modalRef.current.scrollTop;
        localStorage.setItem(`off_market_scroll_position_${actionType}_${willingnessId}`, scrollPosition.toString());
      }
      // Show login modal (keep off-market modal open)
      if (setLoginModalVisible) {
        setLoginModalVisible(true);
      }
      return;
    }
    // Save details expanded state before redirecting to Stripe
    if (isDetailsExpanded) {
      localStorage.setItem(`off_market_details_expanded_${actionType}_${willingnessId}`, 'true');
    }
    // Save scroll position before redirecting to Stripe
    if (modalRef.current) {
      const scrollPosition = modalRef.current.scrollTop;
      localStorage.setItem(`off_market_scroll_position_${actionType}_${willingnessId}`, scrollPosition.toString());
    }
    handleBuyCredits();
  };

  // Lightbox handlers
  const openLightbox = (index: number) => {
    setLightboxInitialIndex(index);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
  };

  if (!isOpen) return null;

  const handleSubmit = () => {
    // Frontend validation: size cannot be 0 or negative if provided
    if (formData.size.trim() !== '' && Number(formData.size) <= 0) {
      setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
      return;
    }
    setSizeError('');
    const propertyDetails: PropertyDetails = {};

    propertyDetails.description = formData.description;
    propertyDetails.size = (typeof formData.size === 'string' && formData.size.trim() === '') ? undefined : Number(formData.size);
    propertyDetails.rooms = formData.rooms;
    propertyDetails.floor = (typeof formData.floor === 'string' && formData.floor.trim() === '') ? undefined : Number(formData.floor);
    propertyDetails.hasElevator = formData.hasElevator;
    propertyDetails.hasBalcony = formData.hasBalcony;
    propertyDetails.renovationStatus = formData.renovationStatus;
    propertyDetails.buildingType = formData.buildingType;

    // Add asking price if provided (sell flow only)
    if (actionType === 'sell' && askingPrice.trim() !== '') {
      propertyDetails.askingPrice = Number(askingPrice);
    }

    // Add price estimation ID if available
    if (estimationId) {
      propertyDetails.priceEstimationId = estimationId;
      propertyDetails.priceEstimationStatus = estimationStatus;
    }

    // Only include successfully uploaded images (those that have an id and no error)
    const validImages = formData.images.filter(img => img.id && !img.error && !img.isUploading);
    // In edit mode, always include images field (even if empty) so backend knows to update
    if (isEditMode) {
      propertyDetails.images = validImages;
    } else if (validImages.length > 0) {
      propertyDetails.images = validImages;
    }

    // Save template if requested (buy action only)
    if (shouldSaveTemplate && actionType === 'buy' && isLoggedIn) {
      saveBuyingTemplate();
    }

    // Reset form only after a logged-in submit. If a guest is sent to auth,
    // keep their entered details so they can confirm again after logging in.
    if (isLoggedIn) {
      clearFormData();
      localStorage.removeItem(`off_market_details_expanded_${actionType}_${willingnessId}`);
      localStorage.removeItem(`off_market_scroll_position_${actionType}_${willingnessId}`);
    }

    onConfirm(propertyDetails);
  };

  const resetForm = () => {
    clearFormData();
    setIsDetailsExpanded(false);
    // Also clean up any saved UI state
    localStorage.removeItem(`off_market_details_expanded_${actionType}_${willingnessId}`);
    localStorage.removeItem(`off_market_scroll_position_${actionType}_${willingnessId}`);
    onClose();
  };

  const toggleDetailsExpansion = () => {
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  // Update form field handlers
  const updateFormField = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteBuyingTemplate?.();
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Handle scrolling to bottom when images are selected
  const handleImagesSelected = () => {
    // Ensure details section is expanded to show images
    if (!isDetailsExpanded && !isEditMode) {
      setIsDetailsExpanded(true);
    }

    // Small delay to ensure DOM updates and images start loading
    setTimeout(() => {
      if (modalRef.current) {
        // Smooth scroll the modal itself to the bottom (it's the scrollable container)
        modalRef.current.scrollTo({
          top: modalRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 300); // Slightly longer delay to account for expansion animation
  };

  // Use createPortal to render outside the parent component hierarchy
  return ReactDOM.createPortal(
    <div className={`off-market-modal-overlay ${isLandingPage ? 'off-market-modal-overlay--landing-page' : ''}${elevated ? ' off-market-modal-overlay--elevated' : ''}`}>
      <div className="off-market-modal" ref={modalRef}>
        <div className="off-market-modal__close-button-wrapper">
          <button
            className="off-market-modal__close-button"
            onClick={resetForm}
            disabled={isLoading}
          >
            <CloseIcon size={20} />
          </button>
        </div>
        <div className="off-market-modal__header">
          <h2 className="off-market-modal__title">
            {actionType === 'sell' ? (
              <OffMarketSellIcon
                width="26px"
                height="26px"
                className="off-market-modal__sell-icon"
              />
            ) : (
              <OffMarketBuyIcon
                width="26px"
                height="26px"
                className="off-market-modal__buy-icon"
              />
            )}
            {isEditMode ? (
              <Trans i18nKey="building.offMarket.modal.edit.title" />
            ) : (
              <Trans
                i18nKey={`building.offMarket.modal.${actionType}.title`}
              />
            )}
          </h2>
        </div>

        <div className="off-market-modal__content">
          <p className={`off-market-modal__description ${isEditMode ? 'off-market-modal__description--edit' : ''}`}>
            {isEditMode ? (
              <Trans
                i18nKey={`building.offMarket.modal.edit.${actionType}Description`}
              />
            ) : (
              <Trans
                i18nKey={`building.offMarket.modal.${actionType}.description`}
              />
            )}
          </p>
          {actionType === 'buy' && isUsingTemplate && (
            <div className="template-chip">
              <span className="template-chip__text">
                {t('building.offMarket.template.usingTemplate')}
              </span>
              <button
                className="template-chip__clear"
                onClick={requestDelete}
                disabled={isDeleting}
                aria-label={t('building.offMarket.template.clear')}
              >
                {isDeleting ? (
                  <div className="template-chip__clear-spinner" />
                ) : (
                  <svg id="fi_3096673"
                    enableBackground="new 0 0 512 512"
                    height="13" viewBox="0 0 512 512"
                    width="13"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="#fff"
                  >
                    <g><path
                      d="m424 64h-88v-16c0-26.467-21.533-48-48-48h-64c-26.467 0-48 21.533-48 48v16h-88c-22.056 0-40 17.944-40 40v56c0 8.836 7.164 16 16 16h8.744l13.823 290.283c1.221 25.636 22.281 45.717 47.945 45.717h242.976c25.665 0 46.725-20.081 47.945-45.717l13.823-290.283h8.744c8.836 0 16-7.164 16-16v-56c0-22.056-17.944-40-40-40zm-216-16c0-8.822 7.178-16 16-16h64c8.822 0 16 7.178 16 16v16h-96zm-128 56c0-4.411 3.589-8 8-8h336c4.411 0 8 3.589 8 8v40c-4.931 0-331.567 0-352 0zm313.469 360.761c-.407 8.545-7.427 15.239-15.981 15.239h-242.976c-8.555 0-15.575-6.694-15.981-15.239l-13.751-288.761h302.44z"></path><path d="m256 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z"></path><path d="m336 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z"></path><path d="m176 448c8.836 0 16-7.164 16-16v-208c0-8.836-7.164-16-16-16s-16 7.164-16 16v208c0 8.836 7.163 16 16 16z">
                      </path></g></svg>
                )}
              </button>
            </div>
          )}

          <div className="off-market-modal__form">

            {!isEditMode && (
              <div className={`off-market-modal__expander ${actionType === 'buy' && isDetailsExpanded ? 'off-market-modal__expander--buy' : ''}`}>
                <button
                  onClick={toggleDetailsExpansion}
                  className="off-market-modal__expander-button"
                >
                  <Trans i18nKey="building.offMarket.modal.additionalDetails" />
                  <ExpanderArrow isExpanded={isDetailsExpanded} />
                </button>
              </div>
            )}


            <CSSTransition
              in={isDetailsExpanded}
              timeout={300}
              classNames="details-expand"
              unmountOnExit
              onEntered={() => {
                // If we have a saved scroll position, attempt to restore it as soon as the expand animation finishes
                const savedScrollPosition = localStorage.getItem(`off_market_scroll_position_${actionType}_${willingnessId}`);
                if (savedScrollPosition && modalRef.current) {
                  const target = parseInt(savedScrollPosition, 10);
                  let attempts = 0;
                  const maxAttempts = 10; // ~1s with 100ms intervals
                  const tryRestore = () => {
                    if (!modalRef.current) return;
                    modalRef.current.scrollTop = target;
                    // Verify and retry if not yet applied (due to ongoing layout)
                    if (Math.abs(modalRef.current.scrollTop - target) > 2 && attempts < maxAttempts) {
                      attempts += 1;
                      setTimeout(tryRestore, 100);
                    } else {
                      localStorage.removeItem(`off_market_scroll_position_${actionType}_${willingnessId}`);
                    }
                  };
                  // Small delay to ensure final layout after onEntered
                  setTimeout(tryRestore, 0);
                }
              }}
            >
              <div className={`off-market-modal__additional-details
                 ${isTemplateAnimating && actionType === 'buy' ? 'off-market-modal__additional-details--animating' : ''}
                 ${actionType === 'sell' ? 'off-market-modal__additional-details--sell' : ''}`}>
                {/* Template indicator for buy action */}
                {actionType === 'buy' && isLoggedIn && !isEditMode && (
                  <BuyingTemplateIndicator
                    hasTemplate={hasTemplate}
                    shouldSaveTemplate={shouldSaveTemplate}
                    onToggleSaveTemplate={setShouldSaveTemplate}
                    isLoadingTemplate={isLoadingTemplate}
                    isTemplateAnimating={isTemplateAnimating}
                  />
                )}

                <div className="off-market-modal__form-field">
                  <label htmlFor="property-description">
                    <Trans i18nKey="building.offMarket.modal.description" />
                  </label>
                  <textarea
                    id="property-description"
                    className="off-market-modal__textarea"
                    value={formData.description}
                    onChange={(e) => updateFormField('description', e.target.value)}
                    placeholder={actionType === 'buy' ? t('building.offMarket.modal.buyingInterestDescriptionPlaceholder') : t('building.offMarket.modal.sellingInterestDescriptionPlaceholder')}
                    rows={3}
                    maxLength={1100}
                  />
                  <span className="off-market-modal__textarea-counter">{formData.description.length} / 1100</span>
                </div>
                <div className="off-market-modal__form-row">
                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-size">
                      {actionType === 'buy' ? <Trans i18nKey="building.offMarket.modal.sizeClass" /> : <Trans i18nKey="building.offMarket.modal.size" />}
                    </label>
                    <input
                      id="property-size"
                      type="number"
                      className="off-market-modal__input"
                      value={formData.size}
                      onChange={(e) => {
                        const next = e.target.value;
                        updateFormField('size', next);
                        if (next.trim() === '') {
                          setSizeError('');
                        } else if (Number(next) === 0) {
                          setSizeError(t('building.offMarket.modal.sizeMustBeGreaterThanZero'));
                        } else {
                          setSizeError('');
                        }
                      }}
                      placeholder={t('building.offMarket.modal.sizePlaceholder')}
                      min="1"
                    />
                    {sizeError && (
                      <div className="off-market-modal__input-error" role="alert">
                        {sizeError}
                      </div>
                    )}
                  </div>

                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-rooms">
                      <Trans i18nKey="building.offMarket.modal.roomsLabel" />
                    </label>
                    <select
                      id="property-rooms"
                      className="off-market-modal__select"
                      value={formData.rooms}
                      onChange={(e) => updateFormField('rooms', e.target.value)}
                    >
                      <option value="">
                        {t('building.offMarket.modal.selectRooms')}
                      </option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5+">5+</option>
                    </select>
                  </div>
                </div>
                <div className="off-market-modal__form-row">
                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-floor">
                      <Trans i18nKey="building.offMarket.modal.floor" />
                    </label>
                    <input
                      id="property-floor"
                      type="number"
                      className="off-market-modal__input"
                      value={formData.floor}
                      onChange={(e) => updateFormField('floor', e.target.value)}
                      placeholder={t('building.offMarket.modal.floorPlaceholder')}
                      min="0"
                    />
                  </div>

                  <div className="off-market-modal__form-field off-market-modal__form-field--half">
                    <label htmlFor="property-renovation">
                      <Trans i18nKey="building.offMarket.modal.renovation" />
                    </label>
                    <select
                      id="property-renovation"
                      className="off-market-modal__select"
                      value={formData.renovationStatus}
                      onChange={(e) => updateFormField('renovationStatus', e.target.value)}
                    >
                      <option value="">
                        {t('building.offMarket.modal.selectRenovation')}
                      </option>
                      <option value="recently_renovated">
                        {t('building.offMarket.modal.recentlyRenovated')}
                      </option>
                      <option value="good_condition">
                        {t('building.offMarket.modal.goodCondition')}
                      </option>
                      <option value="needs_renovation">
                        {t('building.offMarket.modal.needsRenovation')}
                      </option>
                    </select>
                  </div>
                </div>

                <div className="off-market-modal__form-row off-market-modal__form-row--checkboxes">
                  <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                    <input
                      id="property-elevator"
                      type="checkbox"
                      checked={formData.hasElevator}
                      onChange={(e) => updateFormField('hasElevator', e.target.checked)}
                    />
                    <label htmlFor="property-elevator">
                      <Trans i18nKey="building.offMarket.modal.hasElevator" />
                    </label>
                  </div>

                  <div className="off-market-modal__form-field off-market-modal__form-field--checkbox">
                    <input
                      id="property-balcony"
                      type="checkbox"
                      checked={formData.hasBalcony}
                      onChange={(e) => updateFormField('hasBalcony', e.target.checked)}
                    />
                    <label htmlFor="property-balcony">
                      <Trans i18nKey="building.offMarket.modal.hasBalcony" />
                    </label>
                  </div>
                </div>

                {/* Image Upload Section */}
                {actionType === 'sell' && (
                  <PropertyImageUpload
                    images={formData.images}
                    setImages={(newImages) => {
                      if (typeof newImages === 'function') {
                        setFormData(prev => ({ ...prev, images: newImages(prev.images) }));
                      } else {
                        updateFormField('images', newImages);
                      }
                    }}
                    maxImages={MAX_IMAGES}
                    enhancementStatus={enhancementStatus}
                    onEnhancementStatusUpdate={setEnhancementStatus}
                    onOpenLightbox={openLightbox}
                    onBuyCredits={handleBuyCreditsWithLoginCheck}
                    isPurchasingCredits={isPurchasingCredits}
                    isLoggedIn={isLoggedIn || false}
                    setLoginModalVisible={setLoginModalVisible}
                    onImagesSelected={handleImagesSelected}
                    onUploadingChange={setIsLoadingImages}
                  />
                )}
              </div>
            </CSSTransition>
          </div>
        </div>

        <div className="off-market-modal__footer">
          <button
            className="off-market-modal__button off-market-modal__button--cancel"
            onClick={resetForm}
          >
            <Trans i18nKey="building.offMarket.modal.cancel" />
          </button>
          <button
            className="off-market-modal__button off-market-modal__button--confirm"
            onClick={handleSubmit}
            disabled={isLoading || isLoadingImages}
          >
            {isLoading || isLoadingImages ? (
              <div className="off-market-modal__button-spinner"></div>
            ) : (
              isEditMode ? (
                <Trans i18nKey="building.offMarket.modal.update" />
              ) : (
                <Trans i18nKey="building.offMarket.modal.confirm" />
              )
            )}
          </button>
        </div>
      </div>
      <ImageLightbox
        isOpen={isLightboxOpen}
        images={formData.images}
        initialIndex={lightboxInitialIndex}
        onClose={closeLightbox}
        onToggleVersion={toggleImageVersion}
        onRegenerate={regenerateImage}
        isRegenerating={isRegenerating}
        enhancementCredits={enhancementStatus?.credits ?? null}
      />
      {isPriceReportOpen && estimation && (
        <PriceEstimationReport
          isOpen={isPriceReportOpen}
          onClose={() => setIsPriceReportOpen(false)}
          estimation={estimation}
        />
      )}
      {isPreEstimationModalOpen && (
        <PreEstimationModal
          setIsPreEstimationModalOpen={setIsPreEstimationModalOpen}
          updateFormField={updateFormField}
          startEstimation={startEstimation}
          formData={formData}
          setForceShowPreFields={setForceShowPreFields}
          setSizeError={setSizeError}
          sizeError={sizeError}
          isLoggedIn={isLoggedIn}
          setLoginModalVisible={setLoginModalVisible}
        />
      )}
      {actionType === 'buy' && showDeleteConfirm && (
        <div className="end-chat-confirmation-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
          <div className="end-chat-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="end-chat-confirmation__header">
              <h3>
                <Trans i18nKey="building.offMarket.template.deleteConfirmTitle">Remove template</Trans>
              </h3>
              <button
                className="end-chat-confirmation__close"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <CloseIcon color="#202125" size={16} />
              </button>
            </div>
            <div className="end-chat-confirmation__content">
              <p>
                <Trans i18nKey="building.offMarket.template.deleteConfirmText">Are you sure you want to remove this template?</Trans>
              </p>
            </div>
            <div className="end-chat-confirmation__actions">
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <Trans i18nKey="building.offMarket.template.cancel">Cancel</Trans>
              </button>
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t('building.offMarket.template.loading', 'Loading...') : <Trans i18nKey="building.offMarket.template.delete">Remove</Trans>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body // Render directly to the body element
  );
};

export default OffMarketBuyAndSellDetailsModal;